import { useEffect, useMemo, useRef, useState } from "react";
import { Boxes, Layers3, MapPinned, Move, Pencil, Save, ZoomIn, ZoomOut } from "lucide-react";
import "@/aion-video-capabilities.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DevicePanel } from "@/components/DevicePanel";
import { getSpatialRoomCameraInsights } from "@/services/api";
import { getSpatialHomeLayout, saveSpatialHomeLayout } from "@/services/spatialHome";

import { getFloorScale, getRoomMeasurements, moveSpatialItem, nextSpatialElementId, parseFloorDimensions, parseSpatialDrag, spatialPointerPosition } from "@/lib/spatialLayout";

const FLOOR_CAPACITY = 16;
const GEOMETRY_CAPACITY = 256;
const DRAG_FORMAT = "application/x-aion-spatial";

const floorIdFrom = (name) => `floor-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || Date.now()}`;

const nextPlacement = (floorId, roomId, existing) => {
  const index = existing.filter((placement) => placement.floor_id === floorId).length;
  if (index >= FLOOR_CAPACITY) return null;
  const column = index % 4;
  const row = Math.floor(index / 4);
  return {
    room_id: roomId,
    floor_id: floorId,
    x: 3 + (column * 24),
    y: 5 + (row * 23),
    width: 21,
    height: 18,
  };
};

const deviceKind = (device) => String(device?.device_type || device?.type || "").toLowerCase();
const hasNumericValue = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
const temperatureText = (value) => hasNumericValue(value) ? `${Number(value).toFixed(1)} °C` : "—";
const humidityText = (value) => hasNumericValue(value) ? `${Number(value).toFixed(0)} %` : "—";

export const cameraInsightAgeLabel = (timestamp, nowMs = Date.now()) => {
  const parsed = Date.parse(timestamp || "");
  if (!Number.isFinite(parsed)) return "Zeit unbekannt";
  const ageMs = Math.max(0, nowMs - parsed);
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days === 1 ? "" : "en"}`;
};

export const AionSpatialHomePanel = ({ devices, token, onRefresh, profileAccessMode = "self", profileScopeKey = "self" }) => {
  const [layout, setLayout] = useState({ floors: [], placements: [], walls: [], openings: [], available_rooms: [] });
  const [selectedFloorId, setSelectedFloorId] = useState("");
  const [focusedRoomId, setFocusedRoomId] = useState("");
  const [newFloorName, setNewFloorName] = useState("Erdgeschoss");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [editingLayout, setEditingLayout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dimensionDraft, setDimensionDraft] = useState({ width: "", depth: "" });
  const [focusedGeometry, setFocusedGeometry] = useState(null);
  const [cameraInsights, setCameraInsights] = useState(null);
  const [cameraInsightsLoading, setCameraInsightsLoading] = useState(false);
  const [cameraInsightsError, setCameraInsightsError] = useState("");
  const [cameraInsightsRefreshKey, setCameraInsightsRefreshKey] = useState(0);
  const stageRef = useRef(null);
  const pointerDrag = useRef(null);
  const sharedProfileReadOnly = profileAccessMode === "shared_view";
  const canEdit = !sharedProfileReadOnly && editingLayout && !pending && !loading;

  const rooms = useMemo(() => devices?.rooms || [], [devices?.rooms]);
  const roomMap = useMemo(() => Object.fromEntries(rooms.map((room) => [room.id, room])), [rooms]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const next = await getSpatialHomeLayout(token);
        if (cancelled) return;
        setLayout(next);
        const firstFloorId = next.floors?.[0]?.id || "";
        setSelectedFloorId((current) => current || firstFloorId);
        setDirty(false);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "Spatial Home konnte nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [token]);

  const selectedFloor = layout.floors.find((floor) => floor.id === selectedFloorId) || layout.floors[0] || null;
  const selectedFloorKey = selectedFloor?.id;
  const floorWidth = selectedFloor?.width_m;
  const floorDepth = selectedFloor?.depth_m;
  useEffect(() => {
    setDimensionDraft({ width: String(floorWidth ?? ""), depth: String(floorDepth ?? "") });
    setFocusedGeometry(null);
    pointerDrag.current = null;
  }, [selectedFloorKey, floorWidth, floorDepth]);
  const floorScale = getFloorScale(selectedFloor);
  const geometryCount = (layout.walls || []).length + (layout.openings || []).length;
  const selectedGeometry = focusedGeometry
    ? (focusedGeometry.kind === "wall" ? layout.walls : layout.openings)?.find((item) => item.id === focusedGeometry.id && item.floor_id === selectedFloorKey)
    : null;
  const focusedPlacement = layout.placements.find((item) => item.room_id === focusedRoomId && item.floor_id === selectedFloorKey);
  const focusedMeasurements = getRoomMeasurements(focusedPlacement, selectedFloor);

  const placements = selectedFloor ? layout.placements.filter((placement) => placement.floor_id === selectedFloor.id) : [];
  const floorWalls = selectedFloor ? (layout.walls || []).filter((wall) => wall.floor_id === selectedFloor.id) : [];
  const floorOpenings = selectedFloor ? (layout.openings || []).filter((opening) => opening.floor_id === selectedFloor.id) : [];
  const assignedRoomIds = useMemo(() => new Set(layout.placements.map((placement) => placement.room_id)), [layout.placements]);
  const unassignedRooms = rooms.filter((room) => !assignedRoomIds.has(room.id));
  const focusedRoom = focusedRoomId ? roomMap[focusedRoomId] || null : null;
  const focusedDevices = focusedRoom ? { rooms: [focusedRoom] } : { rooms: [] };
  const focusedTelemetry = useMemo(() => {
    const roomDevices = focusedRoom?.devices || [];
    const thermostats = roomDevices.filter((device) => deviceKind(device) === "thermostat");
    const cameras = roomDevices.filter((device) => deviceKind(device) === "camera");
    const locks = roomDevices.filter((device) => ["door_lock", "lock"].includes(deviceKind(device)));
    const contactSensors = roomDevices.filter((device) => device.state?.contact === "open" || device.state?.contact === "closed");
    const safetySensors = roomDevices.filter((device) => device.state?.smoke !== undefined || device.state?.water !== undefined);
    const thermostat = thermostats[0] || null;
    const humidityDevice = roomDevices.find((device) => hasNumericValue(device.state?.humidity)) || thermostat;
    const camerasOnline = cameras.filter((device) => String(device.connection_state || "").toLowerCase() === "online" && String(device.trust_status || "").toUpperCase() !== "REVOKED").length;
    const locksLocked = locks.filter((device) => device.state?.lock === "locked").length;
    const openContacts = contactSensors.filter((device) => device.state?.contact === "open").length;
    const activeSafetyAlerts = safetySensors.filter((device) => device.state?.smoke === true || device.state?.water === true).length;
    return {
      currentTemperature: temperatureText(thermostat?.state?.current_temperature ?? thermostat?.state?.temperature),
      targetTemperature: temperatureText(thermostat?.state?.target_temperature),
      humidity: humidityText(humidityDevice?.state?.humidity),
      cameraStatus: cameras.length ? `${camerasOnline}/${cameras.length} online` : "keine Kamera",
      lockStatus: locks.length ? `${locksLocked}/${locks.length} verriegelt` : "keine Schlösser",
      contactStatus: contactSensors.length ? (openContacts ? `${openContacts} offen` : "alles geschlossen") : "keine Kontakte",
      safetyStatus: safetySensors.length ? (activeSafetyAlerts ? `${activeSafetyAlerts} Alarm` : "OK") : "keine Sensoren",
    };
  }, [focusedRoom]);

  useEffect(() => {
    let cancelled = false;
    if (!focusedRoomId) {
      setCameraInsights(null);
      setCameraInsightsError("");
      setCameraInsightsLoading(false);
      return () => { cancelled = true; };
    }

    const loadCameraInsights = async () => {
      setCameraInsightsLoading(true);
      setCameraInsights(null);
      setCameraInsightsError("");
      try {
        const next = await getSpatialRoomCameraInsights(focusedRoomId, token, 3);
        if (!cancelled) setCameraInsights(next);
      } catch (insightError) {
        if (!cancelled) {
          setCameraInsights(null);
          setCameraInsightsError(insightError?.message || "Kamera-Insights konnten nicht geladen werden.");
        }
      } finally {
        if (!cancelled) setCameraInsightsLoading(false);
      }
    };

    void loadCameraInsights();
    return () => { cancelled = true; };
  }, [cameraInsightsRefreshKey, focusedRoomId, profileAccessMode, profileScopeKey, token]);

  const addFloor = () => {
    const name = newFloorName.trim();
    if (sharedProfileReadOnly || !name || pending || loading || layout.floors.length >= 12) return;
    const baseId = floorIdFrom(name);
    let id = baseId;
    let suffix = 2;
    while (layout.floors.some((floor) => floor.id === id)) {
      id = `${baseId}-${suffix}`;
      suffix += 1;
    }
    const level = layout.floors.length ? Math.max(...layout.floors.map((floor) => Number(floor.level) || 0)) + 1 : 0;
    setLayout((previous) => ({ ...previous, floors: [...previous.floors, { id, name, level, width_m: 10, depth_m: 10, dimensions_confirmed: false }] }));
    setSelectedFloorId(id);
    setFocusedRoomId("");
    setEditingLayout(true);
    setNewFloorName("");
    setDirty(true);
  };

  const applyFloorDimensions = () => {
    if (!selectedFloor || !canEdit) return;
    const dimensions = parseFloorDimensions(dimensionDraft.width, dimensionDraft.depth);
    if (!dimensions) {
      setError("Bitte Breite und Tiefe zwischen 0,5 und 200 Metern eingeben.");
      return;
    }
    setLayout((previous) => ({
      ...previous,
      floors: previous.floors.map((floor) => floor.id === selectedFloor.id ? { ...floor, ...dimensions, dimensions_confirmed: true } : floor),
    }));
    setDirty(true);
    setError("");
  };

  const assignRoom = (roomId) => {
    if (!selectedFloor || pending || loading || assignedRoomIds.has(roomId) || layout.placements.length >= 96) return;
    const placement = nextPlacement(selectedFloor.id, roomId, layout.placements);
    if (!placement) {
      setError("Diese Etage hat im einfachen Layout bereits 16 Räume. Lege eine weitere Etage an.");
      return;
    }
    setLayout((previous) => ({ ...previous, placements: [...previous.placements, placement] }));
    setFocusedRoomId(roomId);
    setDirty(true);
    setError("");
  };

  const removeRoom = (roomId) => {
    if (!canEdit) return;
    setLayout((previous) => ({ ...previous, placements: previous.placements.filter((placement) => placement.room_id !== roomId) }));
    if (focusedRoomId === roomId) setFocusedRoomId("");
    setDirty(true);
  };

  const moveItem = (kind, id, x, y) => {
    if (!canEdit) return;
    const collection = kind === "room" ? "placements" : kind === "wall" ? "walls" : "openings";
    setLayout((previous) => ({
      ...previous,
      [collection]: (previous[collection] || []).map((item) => {
        if ((kind === "room" ? item.room_id : item.id) !== id || item.floor_id !== selectedFloorKey) return item;
        return moveSpatialItem(item, kind, x, y);
      }),
    }));
    setDirty(true);
  };

  const resizeRoom = (roomId, delta) => {
    if (!canEdit) return;
    setLayout((previous) => ({
      ...previous,
      placements: previous.placements.map((item) => {
        if (item.room_id !== roomId) return item;
        const width = Math.max(10, Math.min(45, Number(item.width) + delta));
        const height = Math.max(10, Math.min(45, Number(item.height) + delta));
        return {
          ...item,
          width,
          height,
          x: Math.min(Number(item.x), 100 - width),
          y: Math.min(Number(item.y), 100 - height),
        };
      }),
    }));
    setDirty(true);
  };

  const addWall = (orientation = "horizontal") => {
    if (!selectedFloor || !canEdit || geometryCount >= GEOMETRY_CAPACITY) return;
    const id = nextSpatialElementId(`wall-${selectedFloor.id}`, [...(layout.walls || []), ...(layout.openings || [])]);
    const wall = { id, floor_id: selectedFloor.id, x: orientation === "horizontal" ? 8 : 48, y: orientation === "horizontal" ? 48 : 8, length: 40, thickness: 1.5, orientation };
    setLayout((previous) => ({ ...previous, walls: [...(previous.walls || []), wall] }));
    setDirty(true);
  };

  const addOpening = (kind = "door") => {
    if (!selectedFloor || !canEdit || geometryCount >= GEOMETRY_CAPACITY) return;
    const id = nextSpatialElementId(`${kind}-${selectedFloor.id}`, [...(layout.walls || []), ...(layout.openings || [])]);
    const opening = { id, floor_id: selectedFloor.id, kind, x: kind === "door" ? 44 : 72, y: kind === "door" ? 45 : 18, width: kind === "door" ? 10 : 14, height: kind === "door" ? 5 : 3 };
    setLayout((previous) => ({ ...previous, openings: [...(previous.openings || []), opening] }));
    setDirty(true);
  };

  const removeGeometry = (kind, elementId) => {
    if (!canEdit) return;
    setFocusedGeometry(null);
    const collection = kind === "wall" ? "walls" : "openings";
    setLayout((previous) => ({ ...previous, [collection]: (previous[collection] || []).filter((item) => item.id !== elementId) }));
    setDirty(true);
  };

  const selectElement = (kind, item) => {
    if (kind === "room") {
      setFocusedRoomId(item.room_id);
      setFocusedGeometry(null);
    } else {
      setFocusedGeometry({ kind, id: item.id });
      setFocusedRoomId("");
    }
  };

  const elementInteraction = (kind, item) => {
    const id = kind === "room" ? item.room_id : item.id;
    const start = (event) => {
      const point = spatialPointerPosition(stageRef.current?.getBoundingClientRect(), event.clientX, event.clientY);
      return point ? { kind, id, floorId: selectedFloorKey, offsetX: point.x - item.x, offsetY: point.y - item.y } : null;
    };
    const endPointer = (event) => {
      if (pointerDrag.current?.pointerId !== event.pointerId) return;
      pointerDrag.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };
    return {
      draggable: canEdit,
      onDragStart: (event) => {
        const drag = canEdit ? start(event) : null;
        if (!drag) { event.preventDefault(); return; }
        selectElement(kind, item);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(DRAG_FORMAT, JSON.stringify(drag));
      },
      onPointerDown: (event) => {
        if (!canEdit || event.pointerType === "mouse" || !event.isPrimary) return;
        const drag = start(event);
        if (!drag) return;
        event.preventDefault();
        selectElement(kind, item);
        pointerDrag.current = { ...drag, pointerId: event.pointerId };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event) => {
        const drag = pointerDrag.current;
        if (!canEdit || drag?.pointerId !== event.pointerId || drag.floorId !== selectedFloorKey) return;
        const point = spatialPointerPosition(stageRef.current?.getBoundingClientRect(), event.clientX, event.clientY, drag.offsetX, drag.offsetY);
        if (point) moveItem(drag.kind, drag.id, point.x, point.y);
      },
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
      onLostPointerCapture: () => { pointerDrag.current = null; },
      onKeyDown: (event) => {
        if (!canEdit) return;
        const step = event.shiftKey ? 5 : 1;
        const offsets = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        if (offsets[event.key]) {
          event.preventDefault();
          selectElement(kind, item);
          const [dx, dy] = offsets[event.key];
          moveItem(kind, id, item.x + dx, item.y + dy);
        } else if (event.key === "Delete" || event.key === "Backspace") {
          event.preventDefault();
          if (kind === "room") removeRoom(id);
          else removeGeometry(kind, id);
        }
      },
    };
  };

  const save = async () => {
    if (sharedProfileReadOnly || pending || loading) return;
    setPending(true);
    setError("");
    try {
      const saved = await saveSpatialHomeLayout({ floors: layout.floors, placements: layout.placements, walls: layout.walls || [], openings: layout.openings || [] }, token);
      setLayout(saved);
      setDirty(false);
      if (!selectedFloorId && saved.floors?.[0]?.id) setSelectedFloorId(saved.floors[0].id);
    } catch (saveError) {
      setError(saveError.message || "Spatial Home konnte nicht gespeichert werden.");
    } finally {
      setPending(false);
    }
  };

  const roomStats = (roomId) => {
    const room = roomMap[roomId] || {};
    const roomDevices = room.devices || [];
    const active = roomDevices.filter((device) => String(device.state?.power || "").toLowerCase() === "on").length;
    const offline = roomDevices.filter((device) => device.connection_state === "offline").length;
    return { room, total: roomDevices.length, active, offline };
  };

  return (
    <section className="aion-spatial-panel" data-testid="aion-spatial-home-panel">
      <div className="aion-spatial-header">
        <div>
          <p className="jarvis-eyebrow">SPATIAL HOME</p>
          <h2 className="jarvis-title">Digital Twin Zuhause</h2>
          <p className="aion-spatial-subtitle">Dein Zuhause nach Etagen und Räumen. Wähle einen Raum, um seine Geräte und vorhandenen Messwerte zu sehen.</p>
        </div>
        <div className="aion-spatial-actions">
          <Button size="sm" variant="outline" onClick={() => setFocusedRoomId("")} disabled={!focusedRoomId} data-testid="spatial-zoom-out">
            <ZoomOut size={16} aria-hidden="true" /> Gesamtansicht
          </Button>
          <Button size="sm" variant={editingLayout ? "default" : "outline"} onClick={() => setEditingLayout((current) => !current)} disabled={sharedProfileReadOnly || pending || loading} data-testid="spatial-edit-toggle">
            {editingLayout ? <Move size={16} aria-hidden="true" /> : <Pencil size={16} aria-hidden="true" />}
            {editingLayout ? "Layout bearbeiten" : "Bearbeiten"}
          </Button>
          <Button size="sm" onClick={save} disabled={sharedProfileReadOnly || pending || loading || !dirty} data-testid="spatial-save-button">
            <Save size={16} aria-hidden="true" /> {pending ? "Speichert…" : "Layout speichern"}
          </Button>
        </div>
      </div>

      {error ? <div className="jarvis-inline-error" data-testid="spatial-home-error">{error}</div> : null}

      <div className="aion-spatial-floor-toolbar">
        <div className="aion-spatial-floor-tabs" data-testid="spatial-floor-tabs">
          {layout.floors.map((floor) => (
            <button
              type="button"
              key={floor.id}
              className={`aion-spatial-floor-tab${selectedFloor?.id === floor.id ? " aion-spatial-floor-tab-active" : ""}`}
              onClick={() => { setSelectedFloorId(floor.id); setFocusedRoomId(""); setFocusedGeometry(null); }}
              data-testid={`spatial-floor-${floor.id}`}
            >
              <Layers3 size={15} aria-hidden="true" /> {floor.name}
            </button>
          ))}
        </div>
        <div className="aion-spatial-add-floor">
          <Input value={newFloorName} onChange={(event) => setNewFloorName(event.target.value)} placeholder="Neue Etage" aria-label="Name der neuen Etage" disabled={sharedProfileReadOnly || pending || loading} data-testid="spatial-floor-name" />
          <Button size="sm" variant="outline" onClick={addFloor} disabled={sharedProfileReadOnly || pending || loading || !newFloorName.trim() || layout.floors.length >= 12} data-testid="spatial-add-floor">+ Etage</Button>
        </div>
      </div>

      {selectedFloor && editingLayout ? (
        <div className="aion-spatial-geometry-toolbar" data-testid="spatial-geometry-toolbar">
          <span>
            <strong>Grundriss-Elemente</strong>
            <small>Elemente ziehen oder mit den Pfeiltasten verschieben. Umschalt bewegt in größeren Schritten; Entf entfernt das gewählte Element.</small>
          </span>
          <div className="aion-spatial-dimensions" data-testid="spatial-floor-dimensions">
            <label>Breite (m)<Input type="number" min="0.5" max="200" step="0.01" value={dimensionDraft.width} onChange={(event) => setDimensionDraft((previous) => ({ ...previous, width: event.target.value }))} disabled={!canEdit} /></label>
            <label>Tiefe (m)<Input type="number" min="0.5" max="200" step="0.01" value={dimensionDraft.depth} onChange={(event) => setDimensionDraft((previous) => ({ ...previous, depth: event.target.value }))} disabled={!canEdit} /></label>
            <Button size="sm" variant="outline" onClick={applyFloorDimensions} disabled={!canEdit} data-testid="spatial-apply-dimensions">Maße übernehmen</Button>
          </div>
          <small>Maße sind manuelle Angaben. Raumflächen werden aus diesen Angaben und den gezeichneten Raumgrößen berechnet.</small>
          <div>
            <Button size="sm" variant="outline" onClick={() => addWall("horizontal")} disabled={!canEdit || geometryCount >= GEOMETRY_CAPACITY} data-testid="spatial-add-wall">+ Wand</Button>
            <Button size="sm" variant="outline" onClick={() => addWall("vertical")} disabled={!canEdit || geometryCount >= GEOMETRY_CAPACITY} data-testid="spatial-add-wall-vertical">+ Wand vertikal</Button>
            <Button size="sm" variant="outline" onClick={() => addOpening("door")} disabled={!canEdit || geometryCount >= GEOMETRY_CAPACITY} data-testid="spatial-add-door">+ Tür</Button>
            <Button size="sm" variant="outline" onClick={() => addOpening("window")} disabled={!canEdit || geometryCount >= GEOMETRY_CAPACITY} data-testid="spatial-add-window">+ Fenster</Button>
          </div>
        </div>
      ) : null}

      {selectedFloor ? (
        <p className="aion-spatial-scale-note" data-testid="spatial-scale-note">
          {floorScale ? `Etage: ${floorScale.width_m} × ${floorScale.depth_m} m · Maßstab nach deinen Angaben` : "Virtuelles Layout · Etagenmaße noch nicht übernommen"}
        </p>
      ) : null}

      {!selectedFloor ? (
        <div className="aion-spatial-empty" data-testid="spatial-layout-empty">
          <MapPinned size={34} aria-hidden="true" />
          <div>
            <strong>Noch kein räumliches Layout eingerichtet</strong>
            <p>Lege zuerst eine Etage an. AION erzeugt keinen erfundenen Grundriss und verändert keine Geräte.</p>
          </div>
        </div>
      ) : (
        <div className="aion-spatial-viewport" data-testid="spatial-viewport">
        <div
          ref={stageRef}
          className={`aion-spatial-stage${focusedRoomId ? " aion-spatial-stage-focused" : ""}${canEdit ? " aion-spatial-stage-editing" : ""}${floorScale ? " aion-spatial-stage-scaled" : ""}`}
          style={floorScale ? { aspectRatio: `${floorScale.width_m} / ${floorScale.depth_m}`, minWidth: `${Math.max(640, 280 * floorScale.width_m / floorScale.depth_m)}px` } : undefined}
          data-testid="spatial-stage"
          onDragOver={(event) => { if (canEdit && Array.from(event.dataTransfer.types).includes(DRAG_FORMAT)) event.preventDefault(); }}
          onDrop={(event) => {
            if (!canEdit) return;
            const drag = parseSpatialDrag(event.dataTransfer.getData(DRAG_FORMAT));
            if (!drag || drag.floorId !== selectedFloorKey) return;
            event.preventDefault();
            const point = spatialPointerPosition(event.currentTarget.getBoundingClientRect(), event.clientX, event.clientY, drag.offsetX, drag.offsetY);
            if (point) moveItem(drag.kind, drag.id, point.x, point.y);
          }}
        >
          <div className="aion-spatial-stage-label"><Layers3 size={15} aria-hidden="true" /> {selectedFloor.name}</div>
          {floorWalls.map((wall) => (
            <button
              type="button"
              key={wall.id}
              className={`aion-spatial-wall aion-spatial-wall-${wall.orientation}`}
              style={{
                left: `${wall.x}%`,
                top: `${wall.y}%`,
                width: wall.orientation === "horizontal" ? `${wall.length}%` : `${wall.thickness}%`,
                height: wall.orientation === "vertical" ? `${wall.length}%` : `${wall.thickness}%`,
              }}
              title={canEdit ? "Wand auswählen oder verschieben" : "Wand"}
              aria-label={`${wall.orientation === "vertical" ? "Senkrechte" : "Waagerechte"} Wand`}
              aria-pressed={focusedGeometry?.id === wall.id}
              disabled={!canEdit}
              {...elementInteraction("wall", wall)}
              onClick={() => selectElement("wall", wall)}
              onDoubleClick={() => removeGeometry("wall", wall.id)}
              data-testid={`spatial-wall-${wall.id}`}
            ></button>
          ))}
          {floorOpenings.map((opening) => (
            <button
              type="button"
              key={opening.id}
              className={`aion-spatial-opening aion-spatial-opening-${opening.kind}`}
              style={{ left: `${opening.x}%`, top: `${opening.y}%`, width: `${opening.width}%`, height: `${opening.height}%` }}
              title={opening.kind === "door" ? "Tür" : "Fenster"}
              aria-label={opening.kind === "door" ? "Tür" : "Fenster"}
              aria-pressed={focusedGeometry?.id === opening.id}
              disabled={!canEdit}
              {...elementInteraction("opening", opening)}
              onClick={() => selectElement("opening", opening)}
              onDoubleClick={() => removeGeometry("opening", opening.id)}
              data-testid={`spatial-opening-${opening.id}`}
            ></button>
          ))}
          {placements.map((placement) => {
            const { room, total, active, offline } = roomStats(placement.room_id);
            const focused = focusedRoomId === placement.room_id;
            const measurements = getRoomMeasurements(placement, selectedFloor);
            return (
              <button
                type="button"
                key={placement.room_id}
                className={`aion-spatial-room${focused ? " aion-spatial-room-focused" : ""}${focusedRoomId && !focused ? " aion-spatial-room-muted" : ""}`}
                style={{ left: `${placement.x}%`, top: `${placement.y}%`, width: `${placement.width}%`, height: `${placement.height}%` }}
                {...elementInteraction("room", placement)}
                aria-label={`${room.name || placement.room_id}${measurements ? `, ${measurements.width} × ${measurements.depth} Meter, ${measurements.area} Quadratmeter nach Plan` : ""}`}
                onClick={() => { setFocusedRoomId(canEdit || !focused ? placement.room_id : ""); setFocusedGeometry(null); }}
                data-testid={`spatial-room-${placement.room_id}`}
              >
                <span className="aion-spatial-room-icon"><Boxes size={17} aria-hidden="true" /></span>
                <strong>{room.name || placement.room_id}</strong>
                <small>{total} Geräte · {active} aktiv{offline ? ` · ${offline} offline` : ""}</small>
                {measurements ? <small className="aion-spatial-room-measurement">{measurements.width} × {measurements.depth} m · {measurements.area} m²</small> : null}
                <span className="aion-spatial-room-zoom"><ZoomIn size={14} aria-hidden="true" /></span>
              </button>
            );
          })}
          {!placements.length ? <div className="aion-spatial-stage-empty">Diese Etage hat noch keine Räume.</div> : null}
        </div>
        </div>
      )}

      {selectedGeometry && canEdit ? (
        <div className="aion-spatial-geometry-selection" data-testid="spatial-geometry-selection">
          <strong>{focusedGeometry.kind === "wall" ? "Wand" : selectedGeometry.kind === "door" ? "Tür" : "Fenster"} ausgewählt</strong>
          <Button size="sm" variant="outline" onClick={() => removeGeometry(focusedGeometry.kind, selectedGeometry.id)} data-testid="spatial-remove-geometry">Element entfernen</Button>
        </div>
      ) : null}

      {selectedFloor && unassignedRooms.length ? (
        <div className="aion-spatial-unassigned" data-testid="spatial-unassigned-rooms">
          <div>
            <strong>Räume zuordnen</strong>
            <small>Ordne vorhandene Räume zu und passe ihre Position und Größe im Bearbeitungsmodus an.</small>
          </div>
          <div className="aion-spatial-room-chips">
            {unassignedRooms.map((room) => (
              <Button key={room.id} size="sm" variant="outline" onClick={() => assignRoom(room.id)} disabled={pending || loading || layout.placements.length >= 96} data-testid={`spatial-assign-${room.id}`}>+ {room.name}</Button>
            ))}
          </div>
        </div>
      ) : null}

      {focusedRoomId ? (
        <div className="aion-spatial-focus-stack" data-testid="spatial-focus-stack">
          <div className="aion-spatial-focus-strip" data-testid="spatial-focus-strip">
            <div>
              <strong>{focusedRoom?.name || focusedRoomId}</strong>
              <small>Geräte und Messwerte für den gewählten Raum.</small>
              {focusedMeasurements ? <small data-testid="spatial-room-dimensions">{focusedMeasurements.width} × {focusedMeasurements.depth} m · {focusedMeasurements.area} m² nach Plan</small> : null}
            </div>
            <div className="aion-spatial-edit-controls">
              <Button size="sm" variant="outline" onClick={() => resizeRoom(focusedRoomId, -2)} disabled={!canEdit} data-testid="spatial-resize-down">− Größe</Button>
              <Button size="sm" variant="outline" onClick={() => resizeRoom(focusedRoomId, 2)} disabled={!canEdit} data-testid="spatial-resize-up">+ Größe</Button>
              <Button size="sm" variant="outline" onClick={() => removeRoom(focusedRoomId)} disabled={!canEdit} data-testid="spatial-remove-room">Aus Layout entfernen</Button>
            </div>
          </div>
          {focusedRoom ? (
            <>
              <div className="aion-compact-status-grid" data-testid="spatial-room-telemetry">
                <div className="aion-compact-status-box" data-testid="spatial-room-temperature"><span>Temperatur</span><strong>{focusedTelemetry.currentTemperature}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-target-temperature"><span>Sollwert</span><strong>{focusedTelemetry.targetTemperature}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-humidity"><span>Luftfeuchte</span><strong>{focusedTelemetry.humidity}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-cameras"><span>Kameras</span><strong>{focusedTelemetry.cameraStatus}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-locks"><span>Schlösser</span><strong>{focusedTelemetry.lockStatus}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-contacts"><span>Kontakte</span><strong>{focusedTelemetry.contactStatus}</strong></div>
                <div className="aion-compact-status-box" data-testid="spatial-room-safety"><span>Rauch/Wasser</span><strong>{focusedTelemetry.safetyStatus}</strong></div>
              </div>
              <div className="aion-note" data-testid="spatial-room-telemetry-note">Raum-Telemetrie und Sicherheitsstatus stammen ausschließlich aus vorhandenen Gerätewerten. Fehlende Daten werden nicht geschätzt; Schloss-/Alarmaktionen werden hier nicht angeboten.</div>
              <div className="aion-spatial-camera-insights" data-testid="spatial-room-camera-insights">
                <div className="aion-spatial-focus-strip">
                  <div>
                    <strong>Kamera-Insights</strong>
                    <small>Read-only Vision-Zusammenfassungen aus vertrauenswürdigen Kameras dieses Raums. Rohbilder werden nicht gespeichert.</small>
                  </div>
                  <div className="aion-spatial-edit-controls">
                    <span className="aion-badge">{cameraInsightsLoading ? "Lädt …" : `${cameraInsights?.online_count ?? 0}/${cameraInsights?.camera_count ?? 0} online`}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCameraInsightsRefreshKey((value) => value + 1)}
                      disabled={cameraInsightsLoading}
                      data-testid="spatial-camera-insights-refresh"
                    >
                      Aktualisieren
                    </Button>
                  </div>
                </div>
                {cameraInsights?.privacy_blocked ? (
                  <div className="aion-note" data-testid="spatial-camera-privacy-blocked">Kamera-Privatsphäre ist aktiv oder dieses Profil darf private Kamera-Analysen nicht lesen.</div>
                ) : cameraInsightsError ? (
                  <div className="aion-note" data-testid="spatial-camera-insights-error">{cameraInsightsError}</div>
                ) : cameraInsights?.insights?.length ? (
                  <div className="aion-spatial-camera-insight-list">
                    {cameraInsights.insights.map((insight) => (
                      <div className="aion-compact-status-box" key={insight.analysis_id} data-testid={`spatial-camera-insight-${insight.analysis_id}`}>
                        <span>{insight.provider_mode === "live_vision" ? "Vision bestätigt" : "Degraded"} · {cameraInsightAgeLabel(insight.timestamp)}</span>
                        <strong>{insight.scene_summary || "Keine bestätigte Szene"}</strong>
                        <small>{insight.person_detected ? `Personen bestätigt: ${insight.person_count || 1}` : "Keine bestätigte Person"} · Confidence {Math.round((insight.confidence || 0) * 100)}%</small>
                      </div>
                    ))}
                  </div>
                ) : !cameraInsightsLoading ? (
                  <div className="aion-note" data-testid="spatial-camera-insights-empty">Noch keine gespeicherten Vision-Analysen für diesen Raum.</div>
                ) : null}
              </div>
              <div data-testid="spatial-focused-device-controls">
                <DevicePanel devices={focusedDevices} token={token} onDeviceUpdate={onRefresh} />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
